// Sending a finished game, if and only if we were told we may.
//
// **The consent check is here, not at the call site.** Submission is triggered
// from several places — a run ending, a versus game ending, a scenario running
// out — and a rule enforced at each of them is a rule that will be missing
// from the fourth. One door, and every caller goes through it.
//
// **It never throws and never blocks a game.** A failed submission is a
// console line: the network is down, the gate expired, the volume is full.
// None of those are the player's problem and none of them should interrupt the
// thing they were doing, so every failure path here ends in a `console.warn`
// and a `false`.

import type { Command } from "src/engine/rules/commands";

import { type Consent, maySubmit } from "./consent";
import {
  buildSubmission,
  type GameKind,
  submissionBytes,
  type Submission
} from "./submission";

export const TELEMETRY_PATH = "/api/telemetry";

/**
 * The client's own copy of the server's cap.
 *
 * Duplicated on purpose, and it is the one number here that is allowed to be:
 * the server's bound is what must hold and the client's is a *courtesy*, so
 * that an over-long game fails as a named console line rather than as a 413
 * nobody sees. They are checked against each other in `submission.test.ts`, so
 * the copy cannot drift into being larger than the thing it is anticipating.
 */
export const MAX_SUBMISSION_BYTES = 1_000_000;

export interface SubmitInput {
  consent: Consent;
  kind: GameKind;
  log: readonly Command[];
  build: string;
  /** Injected in tests; the real caller lets it default. */
  fetcher?: typeof fetch;
  now?: number;
}

/**
 * Send one finished game.
 *
 * Answers what happened rather than throwing, so a caller can report it and
 * carry on: `"sent"`, or the reason it was not.
 */
export async function submitGame(input: SubmitInput): Promise<
  "sent" | "no-consent" | "empty" | "too-big" | "failed"
> {
  // **The first gate, and the only one that is a rule rather than a
  // courtesy.** Everything below is about whether the send would work; this is
  // about whether it is allowed to be attempted at all.
  if (!maySubmit(input.consent) || !input.consent.id) return "no-consent";
  if (input.log.length === 0) return "empty";

  const submission: Submission = buildSubmission({
    client: input.consent.id,
    consent: input.consent.version,
    kind: input.kind,
    build: input.build,
    log: input.log,
    at: input.now ?? Date.now()
  });

  if (submissionBytes(submission) > MAX_SUBMISSION_BYTES) {
    console.warn("telemetry: game too long to submit, skipping");
    return "too-big";
  }

  const send = input.fetcher ?? fetch;
  try {
    const response = await send(TELEMETRY_PATH, {
      method: "POST",
      headers: { "content-type": "application/json" },
      // The cookie is what makes this an authenticated submission, and it is
      // `HttpOnly` — so it rides on the request rather than being read and
      // attached, which is the whole reason the gate stores it that way.
      credentials: "same-origin",
      body: JSON.stringify(submission)
    });
    if (!response.ok) {
      console.warn(`telemetry: server refused the submission (${response.status})`);
      return "failed";
    }
    return "sent";
  } catch (error) {
    console.warn("telemetry: could not send", error);
    return "failed";
  }
}
